/* ============================================================
   component-accordion.js
   Custom Element <edj-accordion> — wrapper progressif autour de <details>
   Lot 3 Batch B — pixel-perfect EDJ
   - Native <details>/<summary> = accessibilité par défaut (sans JS aussi)
   - JS upgrade = animation max-height smooth
   - data-single = ferme les autres accordéons du même parent à l'ouverture
   - prefers-reduced-motion respecté
   ============================================================ */

class EdjAccordion extends HTMLElement {
  constructor() {
    super();
    this._onSummaryClick = this._onSummaryClick.bind(this);
    this._onTransitionEnd = this._onTransitionEnd.bind(this);
  }

  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;
    this.summary = this.details.querySelector('summary');
    this.bodyInner = this.details.querySelector('.edj-accordion__body-inner');
    if (!this.summary || !this.bodyInner) return;

    this.summary.setAttribute('aria-expanded', this.details.open ? 'true' : 'false');

    // Si déjà open au mount, on s'assure que le body a une hauteur "auto" sans transition
    if (this.details.open) {
      this.bodyInner.style.maxHeight = 'none';
      this.bodyInner.style.opacity = '1';
    } else {
      this.bodyInner.style.maxHeight = '0px';
      this.bodyInner.style.opacity = '0';
    }

    this.summary.addEventListener('click', this._onSummaryClick);
    this.bodyInner.addEventListener('transitionend', this._onTransitionEnd);
  }

  disconnectedCallback() {
    if (this.summary) this.summary.removeEventListener('click', this._onSummaryClick);
    if (this.bodyInner) this.bodyInner.removeEventListener('transitionend', this._onTransitionEnd);
  }

  _prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  _enforceSingle() {
    // Si data-single sur le PARENT du <edj-accordion>, on ferme les autres
    const parent = this.parentElement;
    if (!parent || parent.dataset.single !== 'true') return;
    const siblings = parent.querySelectorAll('edj-accordion');
    siblings.forEach((sib) => {
      if (sib !== this && sib.details && sib.details.open) {
        sib._close();
      }
    });
  }

  _onSummaryClick(event) {
    // On gère manuellement l'ouverture pour pouvoir animer
    event.preventDefault();
    if (this.details.open) {
      this._close();
    } else {
      this._enforceSingle();
      this._open();
    }
  }

  _open() {
    if (!this.details || !this.bodyInner) return;
    this.details.open = true;
    this.summary.setAttribute('aria-expanded', 'true');

    if (this._prefersReducedMotion()) {
      this.bodyInner.style.maxHeight = 'none';
      this.bodyInner.style.opacity = '1';
      return;
    }

    this.dataset.animating = 'true';
    // Mesurer la hauteur cible
    this.bodyInner.style.maxHeight = '0px';
    this.bodyInner.style.opacity = '0';
    // force reflow
    void this.bodyInner.offsetHeight;
    const targetHeight = this.bodyInner.scrollHeight;
    this.bodyInner.style.maxHeight = targetHeight + 'px';
    this.bodyInner.style.opacity = '1';
    this._pendingState = 'open';
  }

  _close() {
    if (!this.details || !this.bodyInner) return;
    this.summary.setAttribute('aria-expanded', 'false');

    if (this._prefersReducedMotion()) {
      this.details.open = false;
      this.bodyInner.style.maxHeight = '0px';
      this.bodyInner.style.opacity = '0';
      return;
    }

    this.dataset.animating = 'true';
    // Si maxHeight est "none", il faut d'abord la fixer à scrollHeight pour pouvoir animer
    const currentHeight = this.bodyInner.scrollHeight;
    this.bodyInner.style.maxHeight = currentHeight + 'px';
    void this.bodyInner.offsetHeight;
    this.bodyInner.style.maxHeight = '0px';
    this.bodyInner.style.opacity = '0';
    this._pendingState = 'close';
  }

  _onTransitionEnd(event) {
    if (event.propertyName !== 'max-height') return;
    if (this._pendingState === 'open') {
      this.bodyInner.style.maxHeight = 'none';
    } else if (this._pendingState === 'close') {
      this.details.open = false;
    }
    this._pendingState = null;
    this.dataset.animating = 'false';
  }
}

if (!customElements.get('edj-accordion')) {
  customElements.define('edj-accordion', EdjAccordion);
}
